// Which read may publish to a shared atom (#407). The atoms are module-scope
// (the app mounts no Jotai Provider) while every caller's guard would be
// per-mount, so a hook keeps ONE of these at module scope, beside its atoms,
// and every read takes a turn from it.
//
// Two checks, and a hook calls the one its semantics need:
// - `isLatest`: no newer read has begun. A latest-only hook publishes only
//   from the newest read; a status atom (fetching / failed) is only the newest
//   read's to set.
// - `claimPublish`: the publish watermark. A successful read publishes when
//   nothing newer has published yet, and claiming records it as the newest
//   published. So an older answer can't replace a newer one, and a good
//   answer isn't thrown away because the read that superseded it then failed.
//
// `supersede` retires every read begun so far without starting one (a cart
// write, a cleared autocomplete): none of them is latest any more, and none
// may publish.

export interface ReadTurn {
    isLatest: () => boolean;
    // Call once, when the read has its answer; true means publish it.
    claimPublish: () => boolean;
}

export interface ReadGuard {
    begin: () => ReadTurn;
    supersede: () => void;
}

export const createReadGuard = (): ReadGuard => {
    let requested = 0;
    let published = 0;

    return {
        begin: () => {
            const turn = ++requested;
            return {
                isLatest: () => turn === requested,
                claimPublish: () => {
                    if (turn <= published) return false;
                    published = turn;
                    return true;
                },
            };
        },
        supersede: () => {
            requested += 1;
            published = requested;
        },
    };
};
